import { Button } from "@/components/ui/button";
import { SlidePanel } from "@/components/ui/slide-panel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User } from '@/types';
import { useToast } from "@/components/ui/use-toast";
import { Loader2, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { AdminGuard } from "@/components/PermissionGuard";

type DeleteUserDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onConfirm: (isHardDelete?: boolean) => Promise<void>;
  isDeleting: boolean;
};

export const DeleteUserDialog: React.FC<DeleteUserDialogProps> = ({
  open,
  onOpenChange,
  user,
  onConfirm,
  isDeleting,
}) => {
  const { toast } = useToast();
  const [showHardDeleteOptions, setShowHardDeleteOptions] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");

  if (!user) return null;

  const resetDialog = () => {
    setShowHardDeleteOptions(false);
    setConfirmationText("");
  };

  const handleDialogClose = () => {
    resetDialog();
    onOpenChange(false);
  };

  // Toasts and dialog-close on success are owned by the parent
  // (UsersTab.handleConfirmDeleteUser) and the underlying useUsers.deleteUser
  // hook — both check the actual result. Don't add toasts here or you'll fire
  // "Success" even when the delete failed server-side (onConfirm resolves
  // either way; it doesn't throw on result.success=false). On failure the
  // parent leaves the dialog open so the user can retry or cancel.
  const handleSoftDelete = async () => {
    await onConfirm(false);
  };

  const handleHardDelete = async () => {
    if (confirmationText !== "delete") {
      toast({
        title: "Error",
        description: 'Please type "delete" to confirm hard deletion.',
        variant: "destructive",
      });
      return;
    }

    await onConfirm(true);
  };

  return (
    <SlidePanel
      isOpen={open}
      onClose={handleDialogClose}
      title={
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          Delete User
        </div>
      }
      width="md"
      topOffset="4.5rem"
      showEdgeCloseButton={true}
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Select how you want to delete the user{' '}
          <span className="font-semibold">
            {user.first_name} {user.last_name}
          </span>
        </p>

        {!showHardDeleteOptions ? (
          // Initial delete options
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <h4 className="font-medium text-primary mb-1">Soft Delete (Recommended)</h4>
                <p className="text-sm text-primary/80">
                  Deactivates the user account. User data is preserved and can be restored later.
                </p>
              </div>

              <AdminGuard>
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <h4 className="font-medium text-destructive mb-1">Hard Delete (Permanent)</h4>
                  <p className="text-sm text-destructive/80">
                    Permanently removes user and all associated data from the database.
                    <span className="font-semibold"> This cannot be undone.</span>
                  </p>
                </div>
              </AdminGuard>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 pt-4">
              <Button
                variant="outline"
                onClick={handleDialogClose}
                disabled={isDeleting}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleSoftDelete}
                disabled={isDeleting}
                className="w-full sm:w-auto bg-primary hover:bg-primary/90"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deactivating...
                  </>
                ) : (
                  'Soft Delete'
                )}
              </Button>
              <AdminGuard>
                <Button
                  variant="destructive"
                  onClick={() => setShowHardDeleteOptions(true)}
                  disabled={isDeleting}
                  className="w-full sm:w-auto"
                >
                  Hard Delete
                </Button>
              </AdminGuard>
            </div>
          </div>
        ) : (
          // Hard delete confirmation
          <div className="space-y-4">
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <h4 className="font-medium text-destructive">Permanent Deletion Warning</h4>
              </div>
              <p className="text-sm text-destructive/80 mb-3">
                This will permanently delete <span className="font-semibold">{user.first_name} {user.last_name}</span> and all associated data including:
              </p>
              <ul className="text-sm text-destructive/80 list-disc list-inside space-y-1 mb-3">
                <li>User profile and authentication data</li>
                <li>Team assignments and relationships</li>
                <li>Project assignments and history</li>
                <li>Activity logs and audit trails</li>
              </ul>
              <p className="text-sm font-semibold text-destructive">
                This action cannot be undone and may cause data inconsistencies.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmation" className="text-sm font-medium">
                Type <span className="font-mono bg-muted px-1 rounded">delete</span> to confirm:
              </Label>
              <Input
                id="confirmation"
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="Type 'delete' here"
                className="font-mono"
                disabled={isDeleting}
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowHardDeleteOptions(false)}
                disabled={isDeleting}
                className="w-full sm:w-auto"
              >
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleHardDelete}
                disabled={isDeleting || confirmationText !== "delete"}
                className="w-full sm:w-auto"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting Permanently...
                  </>
                ) : (
                  'Permanently Delete'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </SlidePanel>
  );
};